import { useForm } from 'antd/es/form/Form';
import { ReactComponent as CrossIcon } from 'assets/images/icons/cross.min.svg';
import clsx from 'clsx';
import { AntButton } from 'components/ant/AntButton/AntButton';
import { Modal } from 'components/UI';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';

import { ChannelMarker } from './ChannelMarker/ChannelMarker';
import { TemplateModalFormValues } from './TemplateModal.types';
import s from './TemplatesModal.module.less';
import { TemplatesModalForm } from './TemplatesModalForm/TemplatesModalForm';
import { useTemplatesModal } from './useTemplatesModal';

const TemplatesModalComponent: React.FC = () => {
  const [form] = useForm<TemplateModalFormValues>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedTemplateGroupId, setTemplateGroupId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateFragment | null>(null);

  const {
    changesMadeRef,
    templateModalData,
    handleCloseTemplatesModal,
    handleSendMessage,
    handleValuesChange,
    messageCreateLoading,
    templatesModalVisibility,
    isMobile,
    isGshEmbedded,
    isVisibleToClient,
    isVisibleToPartner,
  } = useTemplatesModal({ form, selectedTemplate, selectedCategoryId, selectedTemplateGroupId });

  const { channels } = useMemo(() => {
    const category = templateModalData?.messengerTemplateCategories.find(({ id }) => selectedCategoryId === id);

    return {
      channels: category?.channels,
    };
  }, [selectedCategoryId, templateModalData?.messengerTemplateCategories]);

  const modalHeader = () => (
    <div className={s.mobileHeader}>
      <span>Message Center Templates</span> <CrossIcon onClick={handleCloseTemplatesModal} />
    </div>
  );

  const mods = { [s.isMobile]: isMobile, [s.isEmbedded]: isGshEmbedded };

  return (
    <Modal
      className={clsx(s.modal, { ...mods })}
      title={isMobile ? modalHeader() : 'Message Center Templates'}
      open={templatesModalVisibility}
      onCancel={handleCloseTemplatesModal}
      width={1000}
      data-testid="TemplatesModal"
      footer={
        <div className={clsx(s.footer, { ...mods })}>
          <div key="channels" className={clsx(s.channels, { ...mods })}>
            {channels?.map((channel) => {
              return <ChannelMarker key={channel} channel={channel} />;
            })}
            {isVisibleToClient ? <span className={s.visibleToCustomer}>Visible to Customer</span> : null}
            {isVisibleToPartner ? <span className={s.visibleToPartner}>Visible to Partner</span> : null}
          </div>
          <AntButton key="send" type="primary" loading={messageCreateLoading} onClick={handleSendMessage}>
            Send Message
          </AntButton>
        </div>
      }
      isMobile={isMobile}
      destroyOnHidden
      centered
    >
      {templateModalData ? (
        <TemplatesModalForm
          form={form}
          isMobile={isMobile}
          changesMadeRef={changesMadeRef}
          templateModalData={templateModalData}
          handleValuesChange={handleValuesChange}
          selectedCategoryId={selectedCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
          selectedTemplate={selectedTemplate}
          setTemplateGroupId={setTemplateGroupId}
          setSelectedTemplate={setSelectedTemplate}
          isVisibleToExternals={isVisibleToClient || isVisibleToPartner}
        />
      ) : null}
    </Modal>
  );
};

export const TemplatesModal = observer(TemplatesModalComponent);
